/**
 * Defect verification permission (TES-1141).
 *
 * Lives in its own module (not in permissions.ts) so the production import
 * graph only pulls in what the defect UI actually uses.
 */

/**
 * Minimal defect shape needed for the verify permission check (TES-1141).
 */
interface DefectVerifyCheckData {
  status: string;
  verifying_qm?: string | null;
  team_id?: string | null;
  /**
   * Who actually set the defect to `resolved` (server-stamped, TES-2213). When loaded, the UI
   * hides the verify button from the resolver (four-eyes). Deliberately NO `assigned_to` fallback:
   * the UI stays permissive (never stricter than the RPC) — the verify_defect guard is the
   * authoritative boundary (COALESCE(resolved_by, assigned_to), Migration 404).
   */
  resolved_by?: string | null;
}

interface DefectVerifyCheckContext {
  /** Whether the current user holds the qualitaetsmanager role */
  isQualitaetsmanager: boolean;
  /**
   * Team IDs the current user is a member of. Optional: when omitted
   * (team data not loaded at the call site), the team boundary is already
   * guaranteed server-side — RLS only SELECTs defects whose team_id is NULL
   * or within get_user_team_ids(), and the defects UPDATE policy enforces
   * the same team clause on write.
   */
  userTeamIds?: readonly string[];
}

function isAssignedVerifier(defect: DefectVerifyCheckData, currentUserId: string): boolean {
  return defect.verifying_qm === currentUserId;
}

/**
 * Separation of duties (TES-2213): the person who set the defect to `resolved` must not verify it.
 * Anchors on the KNOWN real resolver (`resolved_by`, server-stamped) only — no `assigned_to`
 * fallback, so the UI never becomes stricter than the RPC. The verify_defect guard (Migration 404,
 * COALESCE(resolved_by, assigned_to)) is the authoritative boundary; this hides the button as
 * defense in depth when the actor is loaded client-side.
 */
function isDefectSelfExecutor(
  defect: DefectVerifyCheckData,
  currentUserId: string | null | undefined,
): boolean {
  if (!currentUserId) return false;
  return !!defect.resolved_by && defect.resolved_by === currentUserId;
}

function isTeamQmForDefect(
  defect: DefectVerifyCheckData,
  context: DefectVerifyCheckContext,
): boolean {
  if (!context.isQualitaetsmanager) {
    return false;
  }
  // Team boundary: only check when both sides are known client-side.
  // Defects without a team (legacy rows) fall back to the QM role check.
  if (defect.team_id && context.userTeamIds !== undefined) {
    return context.userTeamIds.includes(defect.team_id);
  }
  return true;
}

/**
 * Verify permission for a defect (Owner decision TES-1141, 2026-06-12):
 * - the assigned verifier (verifying_qm === currentUserId) may verify, AND
 * - any user with the QM role may verify defects WITHIN their own team(s)
 *   in one step (no self-assignment detour). No cross-team verification.
 *
 * UI visibility is not security — the defects UPDATE RLS policy
 * (docs/migrations/155, has_permission_in_tenant('defects','manage','full')
 * + team clause) enforces the same boundary server-side.
 */
export function canVerifyDefect(
  defect: DefectVerifyCheckData,
  currentUserId: string | null | undefined,
  context: DefectVerifyCheckContext,
): boolean {
  if (defect.status !== "resolved" || !currentUserId) {
    return false;
  }
  // TES-2213 SoD: the resolver/executor never verifies their own defect (four-eyes).
  if (isDefectSelfExecutor(defect, currentUserId)) {
    return false;
  }
  return isAssignedVerifier(defect, currentUserId) || isTeamQmForDefect(defect, context);
}
